import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { Button } from '@/components/Button/Button';
import { DateTimePicker } from '@/components/DateTimePicker';
import type { ComponentPageData } from '@/features/content/useComponent';
import { ExtensionGroupDialog } from '@/features/content/timeExtensions/ExtensionGroupDialog';
import { ExtensionGroupList } from '@/features/content/timeExtensions/ExtensionGroupList';
import type { TimeExtensionGroup } from '@/features/content/timeExtensions/types';
import { useRosterHashMap } from '@/features/content/timeExtensions/useRosterHashMap';
import { useDialogFocusReturn } from '@/hooks/useDialogFocusReturn';

import { useUpdateComponentSettings } from './hooks/useUpdateComponentSettings';
import styles from './ManageDueDateDialog.module.css';

interface ManageDueDateDialogProps {
  component: ComponentPageData;
  componentId: string;
  courseId: string;
  mode: 'homework' | 'preLecture';
  onOpenChange(nextOpen: boolean): void;
  open: boolean;
}

function getInitialTimestamp(
  component: ComponentPageData,
  mode: 'homework' | 'preLecture',
) {
  const value =
    mode === 'homework'
      ? component.controlPanel.homeworkEndTimeUtcMilli
      : component.controlPanel.preLectureEndTimeUtcMilli;
  const parsed = value ? Number(value) : Date.now();

  return Number.isFinite(parsed) ? parsed : Date.now();
}

function serializeTimeExtensions(groups: TimeExtensionGroup[]) {
  return groups.map((group) => ({
    course_students: group.hashedStudentIds.map((id) => ({ id })),
    display_name: group.displayName,
    extension_end_time_utc_milli: group.endTimeUtcMilli,
  }));
}

export function ManageDueDateDialog({
  component,
  componentId,
  courseId,
  mode,
  onOpenChange,
  open,
}: ManageDueDateDialogProps) {
  const mutation = useUpdateComponentSettings();
  const [closeMode, setCloseMode] = useState<'NOW' | 'LATER'>('LATER');
  const [selectedTimestamp, setSelectedTimestamp] = useState(() =>
    getInitialTimestamp(component, mode),
  );
  const isPreLecture = mode === 'preLecture';
  const [shouldClosePreLecture, setShouldClosePreLecture] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [homeworkTimeExtensions, setHomeworkTimeExtensions] = useState<
    TimeExtensionGroup[]
  >(() => component.controlPanel.homeworkTimeExtensions ?? []);
  const [preLectureTimeExtensions, setPreLectureTimeExtensions] = useState<
    TimeExtensionGroup[]
  >(() => component.controlPanel.preLectureTimeExtensions ?? []);
  const [editingExtensionIndex, setEditingExtensionIndex] = useState<number | null>(
    null,
  );
  const [isExtensionDialogOpen, setIsExtensionDialogOpen] = useState(false);
  const addExtensionGroupRef = useRef<HTMLElement>(null);
  const focusReturn = useDialogFocusReturn();
  const rosterHashMap = useRosterHashMap(open ? courseId : null);
  const activeTimeExtensions = isPreLecture
    ? preLectureTimeExtensions
    : homeworkTimeExtensions;

  useEffect(() => {
    if (!open) {
      return;
    }

    setCloseMode('LATER');
    setSelectedTimestamp(getInitialTimestamp(component, mode));
    setIsAdvancedOpen(false);
    setHomeworkTimeExtensions(component.controlPanel.homeworkTimeExtensions ?? []);
    setPreLectureTimeExtensions(
      component.controlPanel.preLectureTimeExtensions ?? [],
    );
    setEditingExtensionIndex(null);
    setIsExtensionDialogOpen(false);
    setShouldClosePreLecture(
      Boolean(
        component.rawComponent.is_pre_lecture_complete ??
          component.rawComponent.isPreLectureComplete,
      ),
    );
  }, [component, mode, open]);

  function updateActiveTimeExtensions(nextGroups: TimeExtensionGroup[]) {
    if (isPreLecture) {
      setPreLectureTimeExtensions(nextGroups);
      return;
    }

    setHomeworkTimeExtensions(nextGroups);
  }

  function handleSaveExtensionGroup(group: TimeExtensionGroup) {
    const nextGroups = [...activeTimeExtensions];

    if (editingExtensionIndex === null) {
      nextGroups.push(group);
    } else {
      nextGroups[editingExtensionIndex] = group;
    }

    updateActiveTimeExtensions(nextGroups);
  }

  async function handleSave() {
    const resolvedTimestamp =
      closeMode === 'NOW' ? String(Date.now()) : String(selectedTimestamp);

    await mutation.mutateAsync({
      applyChanges: (draft) => {
        if (isPreLecture) {
          const isAlreadyClosed = Boolean(
            draft.is_pre_lecture_complete ?? draft.isPreLectureComplete,
          );

          draft.pre_lecture_end_time_utc_milli = resolvedTimestamp;
          draft.preLectureEndTimeUtcMilli = resolvedTimestamp;
          draft.pre_lecture_time_extensions =
            serializeTimeExtensions(preLectureTimeExtensions);
          draft.preLectureTimeExtensions =
            serializeTimeExtensions(preLectureTimeExtensions);
          draft.is_pre_lecture_complete =
            shouldClosePreLecture || isAlreadyClosed;
          draft.isPreLectureComplete =
            shouldClosePreLecture || isAlreadyClosed;
        } else {
          draft.end_time_utc_milli = resolvedTimestamp;
          draft.endTimeUtcMilli = resolvedTimestamp;
          draft.homework_time_extensions =
            serializeTimeExtensions(homeworkTimeExtensions);
          draft.homeworkTimeExtensions =
            serializeTimeExtensions(homeworkTimeExtensions);
        }
      },
      component,
      componentId,
      courseId,
    });

    onOpenChange(false);
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content} {...focusReturn}>
          <div className={styles.header}>
            <Dialog.Title className={styles.title}>
              {isPreLecture ? 'Pre-Lecture Due Date' : 'Manage Due Date'}
            </Dialog.Title>
            <Dialog.Description asChild>
              <p className={styles.description}>
                {isPreLecture
                  ? 'Schedule when pre-lecture opens and closes, or close it immediately once submissions should stop.'
                  : 'Choose whether this assignment closes now or at a scheduled future time.'}
              </p>
            </Dialog.Description>
          </div>

          <div className={styles.body}>
            <ul className={styles.infoList}>
              <li>Closing the assignment prevents additional submissions.</li>
              {isPreLecture ? (
                <>
                  <li>
                    Closing pre-lecture grades submitted answers, prevents
                    further submissions, and assigns zero to non-submitters.
                  </li>
                  <li>Once closed, the pre-lecture section CANNOT be reopened.</li>
                  <li>
                    To control when to release pre-lecture questions to students
                    use Visibility Settings.
                  </li>
                </>
              ) : (
                <>
                  <li>
                    Answers students have saved are graded when the homework
                    closes, and a question with no saved answer scores 0.
                  </li>
                  <li>
                    To control when to release homework questions to students use
                    Visibility Settings.
                  </li>
                </>
              )}
            </ul>

            {!isPreLecture ? (
              <label className={styles.selectField} htmlFor="homework-close-mode">
                <span>Close homework</span>
                <select
                  id="homework-close-mode"
                  onChange={(event) =>
                    setCloseMode(event.target.value as 'NOW' | 'LATER')
                  }
                  value={closeMode}
                >
                  <option value="NOW">Now</option>
                  <option value="LATER">Later</option>
                </select>
              </label>
            ) : null}

            {isPreLecture || closeMode === 'LATER' ? (
              <DateTimePicker
                id={`${mode}-due`}
                label={isPreLecture ? 'Pre-lecture due time' : 'Homework close time'}
                onChange={(nextTimestamp) => {
                  if (nextTimestamp !== null) {
                    setSelectedTimestamp(nextTimestamp);
                  }
                }}
                value={selectedTimestamp}
              />
            ) : null}

            {isPreLecture ? (
              <div className={styles.closeSection}>
                <label className={styles.checkboxField}>
                  <input
                    checked={shouldClosePreLecture}
                    onChange={(event) =>
                      setShouldClosePreLecture(event.target.checked)
                    }
                    type="checkbox"
                  />
                  <span>Close pre-lecture</span>
                </label>

                {shouldClosePreLecture ? (
                  <>
                    <label
                      className={styles.selectField}
                      htmlFor="pre-lecture-close-mode"
                    >
                      <span>Close timing</span>
                      <select
                        id="pre-lecture-close-mode"
                        onChange={(event) =>
                          setCloseMode(event.target.value as 'NOW' | 'LATER')
                        }
                        value={closeMode}
                      >
                        <option value="NOW">Now</option>
                        <option value="LATER">Later</option>
                      </select>
                    </label>

                    <p aria-live="polite" className={styles.warning} role="alert">
                      Closing the PRE-LECTURE section will grade submitted
                      answers, prevent further submissions, assign zero to
                      non-submitters. Once closed it CANNOT be reopened.
                    </p>
                  </>
                ) : null}
              </div>
            ) : null}

            <section className={styles.advancedSection}>
              <button
                aria-expanded={isAdvancedOpen}
                className={styles.advancedToggle}
                onClick={() => setIsAdvancedOpen((nextOpen) => !nextOpen)}
                type="button"
              >
                <span>{isAdvancedOpen ? '▼' : '▶'} Advanced Options</span>
              </button>

              {isAdvancedOpen ? (
                <div className={styles.advancedBody}>
                  <div className={styles.advancedHeader}>
                    <div>
                      <h3 className={styles.advancedTitle}>
                        Time Extension Groups
                      </h3>
                      <p className={styles.advancedDescription}>
                        Give a group of students a later due date than the rest
                        of the class.
                      </p>
                    </div>
                    <Button
                      onClick={() => {
                        setEditingExtensionIndex(null);
                        setIsExtensionDialogOpen(true);
                      }}
                      ref={addExtensionGroupRef}
                      size="small"
                      variant="secondary"
                    >
                      Add Extension Group
                    </Button>
                  </div>

                  {rosterHashMap.isError ? (
                    <p aria-live="polite" className={styles.error} role="alert">
                      Student names could not be loaded. The students already in
                      each group stay in it.
                    </p>
                  ) : null}

                  <ExtensionGroupList
                    groups={activeTimeExtensions}
                    onEdit={(index) => {
                      setEditingExtensionIndex(index);
                      setIsExtensionDialogOpen(true);
                    }}
                    onRemove={(index) => {
                      updateActiveTimeExtensions(
                        activeTimeExtensions.filter(
                          (_group, groupIndex) => groupIndex !== index,
                        ),
                      );
                    }}
                  />
                </div>
              ) : null}
            </section>

          </div>

          {mutation.isError ? (
            <p aria-live="polite" className={styles.error} role="alert">
              {mutation.error.message}
            </p>
          ) : null}

          <div className={styles.footer}>
            <Dialog.Close asChild>
              <Button className={styles.cancelButton} variant="secondary">
                CANCEL
              </Button>
            </Dialog.Close>
            <Button
              className={styles.saveButton}
              disabled={mutation.isPending}
              onClick={() => {
                void handleSave();
              }}
              variant="primary"
            >
              {mutation.isPending ? 'Saving…' : 'SAVE'}
            </Button>
          </div>

          <ExtensionGroupDialog
            courseWideEndTimeUtcMilli={String(
              isPreLecture || closeMode === 'LATER'
                ? selectedTimestamp
                : Date.now(),
            )}
            focusFallback={() => addExtensionGroupRef.current}
            group={
              editingExtensionIndex === null
                ? null
                : activeTimeExtensions[editingExtensionIndex] ?? null
            }
            onOpenChange={setIsExtensionDialogOpen}
            onSave={handleSaveExtensionGroup}
            open={isExtensionDialogOpen}
            rosterHashMap={rosterHashMap.data}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
